import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocFileUploaderItem } from 'commons/components/components-wrapper/poc-file-uploader-item.component';
import { PocTooltip } from 'commons/components/components-wrapper/poc-tooltip.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { ConfirmationModalComponent, Select, TooltipedTextComponent } from 'commons/components';
import { FileSection, FileUploaderComponent } from 'commons/components/file-uploader';
import { useTranslation } from 'core/i18n/i18n';
import { useState } from 'react';
import { PopupProps, usePopupManager } from 'react-popup-manager';
import { getSingleSelectValue } from '../settings-devices-eap-settings.utils';
import { extractPemFilenames } from './settings-devices-eap-settings-import-modal.helpers';
import {
    EAP_SETTINGS_IMPORT_COLUMN_HEADERS,
    EAP_SETTINGS_IMPORT_DELIMITERS,
    EapSettingsImportColumnHeaders,
    EapSettingsImportDelimiter,
    EapSettingsImportFlowData,
} from './settings-devices-eap-settings-import-modal.model';
import {
    EapSettingsImportConfirmationModalContent,
    EapSettingsImportFieldRow,
    EapSettingsImportFileUploadedLayout,
    EapSettingsImportLayout,
    EapSettingsImportModal,
} from './settings-devices-eap-settings-import-modal.style';
import { useImportModalFileUpload } from './use-import-modal-file-upload.hook';

interface EapSettingsImportModalProps extends PopupProps {
    onImportFlowStarted?: VoidFunction;
    onContinueImport?: (importFlowData: EapSettingsImportFlowData) => void;
}

export const EapSettingsImportModalComponent: React.FC<EapSettingsImportModalProps> = (props) => {
    const { isOpen, onClose, onImportFlowStarted, onContinueImport } = props;

    const { t } = useTranslation();
    const { open: popupManagerOpen } = usePopupManager();

    const [columnHeaders, setColumnHeaders] = useState<EapSettingsImportColumnHeaders>();
    const [delimiter, setDelimiter] = useState<EapSettingsImportDelimiter>(EAP_SETTINGS_IMPORT_DELIMITERS.COMMA);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);

    const {
        filename,
        fileContent,
        uploadProgress,
        uploadStatus,
        isValidFile,
        fileStatus,
        fileTypes,
        onResetFile,
        onLoadFile,
        getUploadStatusText,
    } = useImportModalFileUpload();

    const continueDisabled = !columnHeaders || uploadStatus !== 'uploaded' || !fileContent || !isValidFile;

    const onContinue = () => {
        if (!fileContent || !columnHeaders) {
            return;
        }

        const pemFilenames = extractPemFilenames(fileContent, delimiter);

        onContinueImport?.({
            filename,
            fileContent,
            delimiter,
            columnHeaders,
            pemFilenames,
        });
        onImportFlowStarted?.();
        onClose?.();
    };

    const openConfirmationModal = (params: {
        title: string;
        children: React.ReactNode;
        buttonText: string;
        cancelButtonText: string;
        onConfirm: VoidFunction;
    }) => {
        const { title, children, buttonText, cancelButtonText, onConfirm } = params;

        setIsConfirmationModalOpen(true);
        popupManagerOpen(ConfirmationModalComponent, {
            title,
            children,
            buttonText,
            cancelButtonText,
            actionOnClick: () =>
                new Promise((resolve) => {
                    onConfirm();
                    resolve(true);
                }),
            onClose: () => {
                setIsConfirmationModalOpen(false);
            },
        });
    };

    const openCancelUploadConfirmation = (onConfirm: VoidFunction) => {
        openConfirmationModal({
            title: t('settings.devices.eap-settings.eap-config-list.action.import.modal.cancel-upload.title'),
            children: (
                <span>
                    {t(
                        'settings.devices.eap-settings.eap-config-list.action.import.modal.cancel-upload.description.question'
                    )}
                </span>
            ),
            buttonText: t(
                'settings.devices.eap-settings.eap-config-list.action.import.modal.cancel-upload.button.confirm'
            ),
            cancelButtonText: t('commons.components.modal.modal-dialog.footer.button.cancel'),
            onConfirm,
        });
    };

    const onDismissFile = () => {
        if (uploadStatus === 'uploading') {
            openCancelUploadConfirmation(() => onResetFile());
            return;
        }

        onResetFile();
    };

    const onCloseWithUnsavedChangesCheck = () => {
        if (uploadStatus === 'uploading') {
            openCancelUploadConfirmation(() => {
                onResetFile();
                onClose?.();
            });
            return;
        }

        if (!filename) {
            onClose?.();
            return;
        }

        openConfirmationModal({
            title: t('commons.modal.unsaved-data.title'),
            children: (
                <EapSettingsImportConfirmationModalContent>
                    <span>{t('commons.modal.unsaved-data.description.main')}</span>
                    <span>{t('commons.modal.unsaved-data.description.secondary')}</span>
                </EapSettingsImportConfirmationModalContent>
            ),
            buttonText: t('commons.modal.unsaved-data.button.primary'),
            cancelButtonText: t(
                'settings.devices.eap-settings.eap-config-list.action.import.modal.button.continue-editing'
            ),
            onConfirm: () => onClose?.(),
        });
    };

    return (
        <EapSettingsImportModal
            title={t('settings.devices.eap-settings.eap-config-list.action.import.modal.title')}
            isOpen={isOpen && !isConfirmationModalOpen}
            onClose={onCloseWithUnsavedChangesCheck}
            size="medium"
            footerOptions={[
                <PocButton
                    key="continue"
                    variant="primary"
                    size="medium"
                    disabled={continueDisabled}
                    onClick={onContinue}
                >
                    {t('settings.devices.eap-settings.eap-config-list.action.import.modal.button.continue')}
                </PocButton>,
            ]}
        >
            <EapSettingsImportLayout>
                <EapSettingsImportFieldRow>
                    <PocTypography variant="body1">
                        {t(
                            'settings.devices.eap-settings.eap-config-list.action.import.modal.field.column-headers.label'
                        )}
                    </PocTypography>
                    <Select
                        name="eapColumnHeaders"
                        value={columnHeaders}
                        placeholder={t(
                            'settings.devices.eap-settings.eap-config-list.action.import.modal.field.column-headers.placeholder'
                        )}
                        options={[
                            {
                                value: EAP_SETTINGS_IMPORT_COLUMN_HEADERS.WITH_HEADERS,
                                text: t(
                                    'settings.devices.eap-settings.eap-config-list.action.import.modal.field.column-headers.option.with-headers'
                                ),
                            },
                            {
                                value: EAP_SETTINGS_IMPORT_COLUMN_HEADERS.WITHOUT_HEADERS,
                                text: t(
                                    'settings.devices.eap-settings.eap-config-list.action.import.modal.field.column-headers.option.without-headers'
                                ),
                            },
                        ]}
                        onChange={(_field, value) =>
                            setColumnHeaders(getSingleSelectValue(value) as EapSettingsImportColumnHeaders)
                        }
                    />
                </EapSettingsImportFieldRow>

                <EapSettingsImportFieldRow>
                    <PocTypography variant="body1">
                        {t('settings.devices.eap-settings.eap-config-list.action.import.modal.field.delimiter.label')}
                    </PocTypography>
                    <Select
                        name="eapDelimiter"
                        value={delimiter}
                        placeholder={t(
                            'settings.devices.eap-settings.eap-config-list.action.import.modal.field.delimiter.placeholder'
                        )}
                        options={Object.values(EAP_SETTINGS_IMPORT_DELIMITERS).map((separator) => ({
                            value: separator,
                            text: t(`commons.delimiter.${separator}`),
                        }))}
                        onChange={(_field, value) =>
                            setDelimiter(getSingleSelectValue(value) as EapSettingsImportDelimiter)
                        }
                    />
                </EapSettingsImportFieldRow>

                <FileSection>
                    {filename ? (
                        <EapSettingsImportFileUploadedLayout>
                            <PocFileUploaderItem
                                value={uploadProgress}
                                status={fileStatus}
                                onDismiss={onDismissFile}
                                dismissIconProps={{ id: 'dismissEapSettingsImportFile' }}
                            >
                                <div slot="prefix">
                                    <TooltipedTextComponent
                                        i18nKey="<tooltip>{{filename}}</tooltip>"
                                        tooltipText={filename}
                                        i18nValues={{
                                            filename,
                                        }}
                                    />
                                </div>
                                <div slot="suffix">{getUploadStatusText()}</div>
                            </PocFileUploaderItem>
                            <PocTypography variant="body1">
                                {t(`settings.devices.eap-settings.eap-config-list.action.import.modal.upload.message`)}
                            </PocTypography>
                            <PocTooltip anchor="dismissEapSettingsImportFile">
                                {t('settings.integrations.lms-api-integration.mapping.file-upload.dismiss')}
                            </PocTooltip>
                        </EapSettingsImportFileUploadedLayout>
                    ) : (
                        <FileUploaderComponent
                            title={
                                t('settings.devices.eap-settings.eap-config-list.action.import.modal.upload.title') ??
                                undefined
                            }
                            subtitle={
                                t(
                                    'settings.devices.eap-settings.eap-config-list.action.import.modal.upload.replace-info'
                                ) ?? undefined
                            }
                            buttonText={t(
                                'settings.devices.eap-settings.eap-config-list.action.import.modal.upload.button'
                            )}
                            fileTypes={fileTypes}
                            onLoadFile={onLoadFile}
                            isUploadDisabled={false}
                            multiple={false}
                        />
                    )}
                </FileSection>
            </EapSettingsImportLayout>
        </EapSettingsImportModal>
    );
};
